import * as React from "react"

const TICKER_ITEMS = [
  {
    label: "10 birth control options",
    marker: "\uD83D\uDC95",
    markerClassName: "text-[15px] lg:text-[25px]",
    markerFrameClassName: "w-[16px] lg:w-[25px]",
    markerOffsetClassName: "",
  },
  {
    label: "All FDA-approved",
    marker: "\uD83D\uDC4D",
    markerClassName: "text-[18px] lg:text-[30px]",
    markerFrameClassName: "w-[18px] lg:w-[30px]",
    markerOffsetClassName: "translate-y-[-1px] lg:translate-y-[-2px]",
  },
  {
    label: "Take control of your birth control",
    marker: "\uD83D\uDC83",
    markerClassName: "text-[15px] lg:text-[25px]",
    markerFrameClassName: "w-[16px] lg:w-[25px]",
    markerOffsetClassName: "",
  },
  {
    label: "Affordable + access = Afaxys",
    marker: "\uD83D\uDCB8",
    markerClassName: "text-[15px] lg:text-[25px]",
    markerFrameClassName: "w-[16px] lg:w-[25px]",
    markerOffsetClassName: "",
  },
] as const

const DUPLICATE_SEQUENCE_COUNT = 3

export type TickerVariant = "blue" | "green"

export type TickerProps = {
  className?: string
  variant?: TickerVariant
}

const TickerSequence = React.forwardRef<HTMLDivElement, { duplicate?: boolean }>(
  ({ duplicate }, ref) => (
    <div
      aria-hidden={duplicate || undefined}
      className={[
        "ticker__sequence flex shrink-0 items-center gap-[40px] lg:gap-[80px]",
        duplicate ? "ticker__sequence--duplicate" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      ref={ref}
      role={duplicate ? undefined : "list"}
    >
      {TICKER_ITEMS.map(item => (
        <span
          key={`${duplicate ? "duplicate" : "primary"}-${item.label}`}
          className="flex shrink-0 items-center gap-[6px] lg:gap-[10px]"
          role={duplicate ? undefined : "listitem"}
        >
          <span
            aria-hidden="true"
            className={[
              "ticker__marker flex shrink-0 items-center justify-center leading-none",
              item.markerFrameClassName,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <span
              className={[
                "block font-mono leading-none",
                item.markerClassName,
                item.markerOffsetClassName,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {item.marker}
            </span>
          </span>
          <span className="font-mono text-[12px] leading-none font-semibold uppercase tracking-[0.05em] lg:text-[16px]">
            {item.label}
          </span>
        </span>
      ))}
    </div>
  ),
)

TickerSequence.displayName = "TickerSequence"

export const Ticker: React.FC<TickerProps> = ({
  className,
  variant = "blue",
}) => {
  const primarySequenceRef = React.useRef<HTMLDivElement>(null)
  const [loopDistance, setLoopDistance] = React.useState(0)

  React.useEffect(() => {
    const sequence = primarySequenceRef.current
    const marquee = sequence?.parentElement

    if (!sequence || !marquee) {
      return
    }

    const updateLoopDistance = () => {
      const marqueeStyle = window.getComputedStyle(marquee)
      const sequenceGap =
        Number.parseFloat(marqueeStyle.columnGap || marqueeStyle.gap) || 0
      const sequenceWidth = sequence.scrollWidth || sequence.offsetWidth
      const nextDistance = Math.round(sequenceWidth + sequenceGap)

      setLoopDistance(currentDistance => {
        return currentDistance !== nextDistance ? nextDistance : currentDistance
      })
    }

    updateLoopDistance()

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateLoopDistance)

      return () => {
        window.removeEventListener("resize", updateLoopDistance)
      }
    }

    const resizeObserver = new ResizeObserver(() => {
      updateLoopDistance()
    })

    resizeObserver.observe(sequence)
    resizeObserver.observe(marquee)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const classes = [
    "w-full text-ink-950",
    variant === "green" ? "bg-lime-wash-300" : "bg-accent-500",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  const marqueeStyle = {
    "--ticker-loop-distance": `${loopDistance}px`,
  } as React.CSSProperties

  return (
    <div className={classes}>
      <div
        aria-label="Afaxys birth control highlights"
        className="ticker__viewport flex h-[35px] items-center px-[18px] lg:h-[49px] lg:px-[30px]"
      >
        <div
          className={[
            "ticker__marquee flex w-max items-center gap-10 lg:gap-20",
            loopDistance > 0 ? "ticker__marquee--ready" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={marqueeStyle}
        >
          <TickerSequence ref={primarySequenceRef} />
          {Array.from({ length: DUPLICATE_SEQUENCE_COUNT }).map((_, index) => (
            <TickerSequence key={index} duplicate />
          ))}
        </div>
      </div>
    </div>
  )
}
